import { Formik } from 'formik'
import * as Yup from 'yup'
import { XStack, YStack } from 'tamagui'
import { useToastController } from '@tamagui/toast'
import { Input } from '@components/Input'
import { Button } from '@components/Button'
import { SectionCard } from '@components/Profile/SectionCard'
import { useComponentTranslation } from '@hooks/useComponentTranslation'
import { useUserPasswordUpdate } from '@services/api/graphql/mutations/userPasswordUpdate'

interface IPasswordForm {
  password: string
  passwordConfirmation: string
}

export function PasswordSection() {
  const { t } = useComponentTranslation('Profile')
  const toast = useToastController()
  const { handleUpdatePassword, loading } = useUserPasswordUpdate()

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(6, t('errors.passwordMin'))
      .required(t('errors.required')),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref('password')], t('errors.passwordMismatch'))
      .required(t('errors.required')),
  })

  async function handleSave(
    values: IPasswordForm,
    { resetForm }: { resetForm: () => void },
  ) {
    const result = await handleUpdatePassword(values)
    if (result?.errors?.length) {
      toast.show(t('toast.errorTitle'), {
        message: result.errors.join('\n'),
        type: 'error',
      })
      return
    }

    resetForm()
    toast.show(t('toast.successTitle'), {
      message: t('password.successMessage'),
    })
  }

  return (
    <SectionCard title={t('password.title')}>
      <Formik
        initialValues={{ password: '', passwordConfirmation: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSave}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <YStack gap="$size.spacing-xs">
            <XStack gap="$size.spacing-xs" $sm={{ flexDirection: 'column' }}>
              <YStack flex={1}>
                <Input
                  label={t('password.password')}
                  secureTextEntry
                  value={values.password}
                  onChangeText={handleChange('password')}
                  onBlur={handleBlur('password')}
                  errorMessage={touched.password && errors.password}
                />
              </YStack>
              <YStack flex={1}>
                <Input
                  label={t('password.confirmation')}
                  secureTextEntry
                  value={values.passwordConfirmation}
                  onChangeText={handleChange('passwordConfirmation')}
                  onBlur={handleBlur('passwordConfirmation')}
                  errorMessage={
                    touched.passwordConfirmation && errors.passwordConfirmation
                  }
                />
              </YStack>
            </XStack>

            <XStack justifyContent="flex-end">
              <Button
                variant="primary"
                loading={loading}
                onPress={() => handleSubmit()}
              >
                {t('actions.save')}
              </Button>
            </XStack>
          </YStack>
        )}
      </Formik>
    </SectionCard>
  )
}
